/**
 * Sentinel Response™ - Login Page
 * Supports Sentinel team login and tenant-branded client login.
 */
import { useEffect, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { tenantApi } from '../api/client';
import { ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [branding, setBranding] = useState(null);
  const [orgSlug, setOrgSlug] = useState('');
  const [loadingBranding, setLoadingBranding] = useState(false);
  const [error, setError] = useState('');

  const orgParam = searchParams.get('org');

  useEffect(() => {
    if (orgParam) loadBranding(orgParam);
  }, [orgParam]);

  if (isAuthenticated) return <Navigate to="/" replace />;

  async function loadBranding(slug) {
    setLoadingBranding(true);
    setError('');
    try {
      const res = await tenantApi.getBranding(slug);
      setBranding(res.data);
    } catch (err) {
      setError('Organization not found. Check the URL and try again.');
      setBranding(null);
    } finally {
      setLoadingBranding(false);
    }
  }

  function handleLogin() { login(); }

  function handleOrgLookup(e) {
    e.preventDefault();
    if (orgSlug.trim()) loadBranding(orgSlug.trim().toLowerCase());
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          {branding ? (
            <>
              {branding.logo_url ? (
                <img src={branding.logo_url} alt={branding.name} className="h-14 mx-auto mb-4 object-contain" />
              ) : (
                <div
                  className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: branding.primary_color }}
                >
                  {branding.name[0]}
                </div>
              )}
              <h1 className="text-xl font-semibold text-white">{branding.name}</h1>
              <p className="text-sm text-gray-500 mt-1">Technology Portal</p>
            </>
          ) : (
            <>
              <img src="/logo-sentinel-icon.svg" alt="Sentinel Response" className="w-16 h-16 mx-auto mb-4" />
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <span className="text-xl font-bold text-white tracking-wider">SENTINEL</span>
                <span className="text-xl font-light text-emerald-400 tracking-wider">RESPONSE</span>
                <span className="text-[8px] text-gray-500 -mt-3">™</span>
              </div>
              <p className="text-sm text-gray-500">Managed technology services platform</p>
            </>
          )}
        </div>

        {/* Login card */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
          {branding ? (
            <>
              <button
                onClick={handleLogin}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white text-sm font-medium rounded-lg transition-colors hover:opacity-90"
                style={{ backgroundColor: branding.primary_color }}
              >
                Sign in to {branding.name}
                <ArrowRight className="w-4 h-4" />
              </button>
              <div className="mt-6 pt-4 border-t border-gray-800 flex items-center justify-center gap-2">
                <img src="/logo-sentinel-icon.svg" alt="" className="w-3.5 h-3.5 opacity-40" />
                <span className="text-[10px] text-gray-600 uppercase tracking-wider">
                  Powered by Sentinel Response
                </span>
              </div>
            </>
          ) : (
            <>
              {/* Sentinel team login */}
              <button
                onClick={handleLogin}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors mb-6"
              >
                <img src="/logo-sentinel-icon.svg" alt="" className="w-4 h-4" />
                Sign in as Sentinel team
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 h-px bg-gray-800" />
                <span className="text-xs text-gray-600 uppercase tracking-wider">Client login</span>
                <div className="flex-1 h-px bg-gray-800" />
              </div>

              {/* Org lookup */}
              <form onSubmit={handleOrgLookup}>
                <label className="block text-sm text-gray-400 mb-2">Organization ID</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={orgSlug}
                    onChange={(e) => setOrgSlug(e.target.value)}
                    placeholder="e.g., lincoln-county-so"
                    className="flex-1 px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600"
                  />
                  <button
                    type="submit"
                    disabled={loadingBranding}
                    className="px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm rounded-lg transition-colors"
                  >
                    {loadingBranding ? '...' : 'Go'}
                  </button>
                </div>
              </form>
            </>
          )}

          {error && <div className="mt-4 text-sm text-red-400 text-center">{error}</div>}
        </div>
      </div>
    </div>
  );
}
